import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  // Get language from localStorage or default to Georgian
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('preferredLanguage');
    return savedLanguage || 'ka'; // Default to Georgian
  });

  useEffect(() => {
    // Save language to localStorage whenever it changes
    localStorage.setItem('preferredLanguage', currentLanguage);
  }, [currentLanguage]);

  const translations = {
    en: {
      // Navigation
      home: "Home",
      tours: "Tours",
      about: "About",
      contact: "Contact",
      
      // Home Page
      discover: "Discover Amazing Destinations",
      heroText: "Book Your Dream Vacation:\nWorld Cup Finals,\nUK Culture & Canadian Adventures",
      exploreTours: "Explore Tours",
      whyChooseUs: "Why Choose Us?",
      expertGuides: "Expert Guides",
      expertDesc: "Local experts who know the hidden gems and rich history of each destination",
      bestPrices: "Best Prices",
      pricesDesc: "Quality and unforgettable experiences all at the best price",
      safeSecure: "Safe & Secure",
      safeDesc: "Your safety is our top priority — every experience is fully insured and carefully verified",
      
      // Tours Page
      ourTours: "Our Tour Packages",
      chooseExperience: "Choose from our carefully curated experiences",
      bookNow: "Book Now",
      hours: "hours",
      fullDay: "Full day",
      
      // About Page - Updated Content
      aboutUs: "About Fan & GenZ",
      sportsTourism: "⚽ Sports Tourism Experts",
      sportsTourismDesc: "We’re your all-access pass to the world’s greatest sporting events. Specializing in World Cup 2026 packages, we bring fans closer to the action with premium tickets, exclusive experiences, and seamless travel arrangements.",
      globalSpecialists: "🌍 Global Destination Specialists",
      globalSpecialistsDesc: "From the iconic stadiums of USA to the royal landmarks of UK and the breathtaking landscapes of Canada, we create immersive journeys that combine sports excitement with cultural discovery.",
      whatMakesDifferent: "🎯 What Makes Us Different",
      premiumAccess: "Premium Access",
      premiumAccessDesc: "Exclusive VIP tickets and once-in-a-lifetime stadium experiences",
      curatedStays: "Curated Stays", 
      curatedStaysDesc: "Luxury accommodations in the most exclusive locations",
      seamlessTravel: "Seamless Travel",
      seamlessTravelDesc: "All transportation and logistics perfectly arranged",
      support247: "24/7 Support",
      support247Desc: "Dedicated assistance throughout your journey",
      ourPromise: "💎 Our Promise",
      ourPromiseDesc: "We don't just book trips - we create unforgettable moments. Whether you're cheering at the World Cup final, exploring London's royal palaces, or hiking in the Canadian Rockies, we ensure every detail is perfect.",
      localExpertise: "Authentic Connections",
      localExpertiseDesc: "Insider knowledge and trusted local partners in every destination",
      bestValue: "Best Value", 
      bestValueDesc: "Premium experiences at competitive prices without hidden costs",
      
      // Contact Page
      contactUs: "Contact Us",
      getInTouch: "Get in touch to start your adventure",
      sendMessage: "Send us a Message",
      fullName: "Full Name",
      emailAddress: "Email Address",
      message: "Message",
      sendMessageBtn: "Send Message",
      phone: "Phone",
      email: "Email",
      address: "Address",
      businessHours: "Business Hours",
      followUs: "Follow Us",
      hoursText: "Monday - Friday: 9:00 AM - 6:00 PM\nSaturday: 10:00 AM - 4:00 PM\nSunday: Closed",
      output_success: "✅ Message sent successfully! We'll get back to you soon.",
      output_error: "❌ There was an error sending your message. Please try again later.",
      
      // Footer
      quickLinks: "Quick Links",
      contactInfo: "Contact Info",
      copyright: "© 2025 Fan & GenZ. All rights reserved.",
    },
    ka: {
      // Navigation
      home: "მთავარი",
      tours: "ტურები",
      about: "ჩვენს შესახებ",
      contact: "კონტაქტი",
      
      // Home Page
      discover: "აღმოაჩინეთ საოცარი მიმართულებები",
      heroText: "დაჯავშნეთ თქვენი ოცნების დასვენება:\nმსოფლიო ჩემპიონატის ფინალი,\nდიდი ბრიტანეთის კულტურა და კანადური თავგადასავლები",
      exploreTours: "ტურების ნახვა",
      whyChooseUs: "რატომ გვირჩევენ?",
      expertGuides: "გამოცდილი გიდები",
      expertDesc: "ადგილობრივი ექსპერტები, რომლებიც იცნობენ ყველა საიდუმლო ადგილს და მდიდარ ისტორიას",
      bestPrices: "საუკეთესო ფასები",
      pricesDesc: "საუკეთესო ფასები შეთავსებულია ხარისხთან და გამოცდილებასთან.",
      safeSecure: "უსაფრთხო და საიმედო",
      safeDesc: "ჩვენი მთავარი პრიორიტეტი თქვენი უსაფრთხოებაა — ყველა გამოცდილება დაზღვეულია და შემოწმებული უმაღლესი სტანდარტებით.",
      
      // Tours Page
      ourTours: "ჩვენი ტურების პაკეტები",
      chooseExperience: "აირჩიეთ ჩვენი საგულდაგულოდ შერჩეული გამოცდილებიდან",
      bookNow: "დაჯავშნა",
      hours: "საათი",
      fullDay: "მთელი დღე",
      
      // About Page - Updated Content
      aboutUs: "Fan & GenZ - ის შესახებ",
      sportsTourism: "⚽ სპორტული ტურიზმის ექსპერტები",
      sportsTourismDesc: "ჩვენ ვართ თქვენი სანდო წვდომა მსოფლიოს უდიდეს სპორტულ ღონისძიებებზე. სპეციალიზირებული ვართ 2026 წლის მსოფლიო თასის პაკეტებში — ჩვენ ვახლოებთ გულშემატკივრებს მოვლენების ეპიცენტრთან, პრემიუმ ბილეთებით, ექსკლუზიური გამოცდილებებით და დაუფერხებლად ორგანიზებული მოგზაურობით.",
      globalSpecialists: "🌍 მსოფლიო მოგზაურობების სპეციალისტები",
      globalSpecialistsDesc: "აშშ-ის კულტურული სტადიონებიდან და დიდი ბრიტანეთის სამეფო ღირსშესანიშნაობებიდან — კანადის შთამბეჭდავ ლანდშაფტებამდე, ჩვენ ვქმნით განსაკუთრებულ მოგზაურობებს, რომლებიც აერთიანებს სპორტულ ვნებებს კულტურულ აღმოჩენებთან.",
      whatMakesDifferent: "🎯 რატომ ვართ განსაკუთრებულები",
      premiumAccess: "პრემიუმ წვდომა",
      premiumAccessDesc: "VIP ბილეთები და ექსკლუზიური სტადიონის გამოცდილება",
      curatedStays: "გამორჩეული დასვენება",
      curatedStaysDesc: "ლუქს კლასის საცხოვრებლები პრესტიჟულ ადგილებში",
      seamlessTravel: "მარტივი მოგზაურობა",
      seamlessTravelDesc: "ყველა ტრანსპორტირება და ლოგისტიკა სრულად ორგანიზებულია",
      support247: "24/7 დახმარება",
      support247Desc: "ჩვენი გუნდი თქვენს გვერდითაა მთელი მოგზაურობის განმავლობაში",
      ourPromise: "💎 ჩვენი დაპირება",
      ourPromiseDesc: "ჩვენ არ ვაწყობთ მხოლოდ მოგზაურობებს - ჩვენ ვქმნით დაუვიწყარ მომენტებს. მიუხედავად იმისა, რომ მსოფლიო თასის ფინალში ხართ, ლონდონის სამეფო სასახლეებს ათვალიერებთ თუ კანადის მთებში მიდიხართ, ჩვენ ვუზრუნველვყოფთ ყველა დეტალის სრულყოფილებას.",
      localExpertise: "ადგილობრივი ცოდნა",
      localExpertiseDesc: "შიდა ცოდნა და სანდო პარტნიორები თითოეულ მიმართულებაზე",
      bestValue: "საუკეთესო ღირებულება",
      bestValueDesc: "პრემიუმ გამოცდილებები კონკურენტულ ფასებში — დამალული ხარჯების გარეშე",
      
      // Contact Page
      contactUs: "დაგვიკავშირდით",
      getInTouch: "დაიწყეთ თქვენი მოგზაურობა ჩვენთან ერთად",
      sendMessage: "გამოგვიგზავნეთ შეტყობინება",
      fullName: "სრული სახელი",
      emailAddress: "ელფოსტის მისამართი",
      message: "შეტყობინება",
      sendMessageBtn: "შეტყობინების გაგზავნა",
      phone: "ტელეფონი",
      email: "მეილი",
      address: "მისამართი",
      businessHours: "სამუშაო საათები",
      followUs: "მოგვყევით",
      hoursText: "ორშაბათი - პარასკევი: 9:00 - 18:00\nშაბათი: 10:00 - 16:00\nკვირა: დახურული",
      output_success: "✅ შეტყობინება წარმატებით გაიგზავნა! მალე დაგიკავშირდებით.",
      output_error: "❌ თქვენი შეტყობინების გაგზავნისას შეცდომა დაფიქსირდა. გთხოვთ, სცადოთ მოგვიანებით.",
      
      // Footer
      quickLinks: "სწრაფი ბმულები",
      contactInfo: "საკონტაქტო ინფორმაცია",
      copyright: "© 2025 Fan & GenZ. ყველა უფლება დაცულია.",
    }
  };

  const switchLanguage = (lang) => {
    setCurrentLanguage(lang);
  };

  const value = {
    currentLanguage,
    switchLanguage,
    t: (key) => translations[currentLanguage][key] || key
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};